import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

export default function Login({ auth }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      // Read response text first to avoid body stream issues
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Parse error:', parseError);
        throw new Error('Something went wrong. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid email or password');
      }

      auth.login(data);
      toast.success("Welcome back");
      navigate('/dashboard');
    } catch (error) {
      // Handle network errors vs server errors
      let message = 'Something went wrong. Please try again.';
      
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        message = 'Unable to connect. Please check your internet connection.';
      } else if (error.message?.toLowerCase().includes('invalid') || 
                 error.message?.toLowerCase().includes('credentials') ||
                 error.message?.toLowerCase().includes('password')) {
        message = 'Invalid email or password';
      } else if (error.message) {
        message = error.message;
      }
      
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <Link to="/" className="block text-center mb-12">
          <h1 
            className="text-3xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            the soft ritual
          </h1>
        </Link>

        {/* Card */}
        <div className="paper-card rounded-3xl p-8 md:p-10">
          <h2 
            className="text-2xl font-normal text-[#2D2A26] text-center mb-2"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Welcome back
          </h2>
          <p className="text-[#6E6A66] text-center font-light mb-8">
            Return to your quiet space
          </p>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#6E6A66] font-light">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="rounded-xl py-6 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#6E6A66] font-light">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-[#A87C6D] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="rounded-xl py-6 pr-12 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A4A0] hover:text-[#6E6A66] transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full rounded-full py-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white text-lg"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="text-center text-[#6E6A66] font-light mt-6">
            New here?{" "}
            <Link to="/register" className="text-[#A87C6D] hover:underline">
              Create an account
            </Link>
          </p>
        </div>

        {/* Back link */}
        <p className="text-center mt-8">
          <Link to="/" className="text-[#A8A4A0] hover:text-[#6E6A66] text-sm">
            ← Back to home
          </Link>
        </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
